import {customElement, property, state} from 'lit/decorators.js';
import {css, CSSResult, html, nothing, PropertyValues, unsafeCSS,} from 'lit';
import {consume} from '@lit/context';
import styles from './product-action-menu.scss';
import {Article, ProductIdentifierContainer, Product} from '../../../@types/wishlist.js';
import typographyStyles from '../../../styles/responsive-nui-typography.scss';
import '../action-menu/action-menu-item.js';
import '../wishlist-image/wishlist-image.js';
import {wishlistService} from '../../../scripts/service/wishlist-service.js';
import {
  trackClickAndReserveEvent,
  trackProductActionMenuOpen,
  trackRemoveFromWishlist,
  trackSnackbarUndoWishlistItemDelete
} from '../../../scripts/service/cube-tracking-layer.js';
import {sizeLayerContext, SizeLayerState} from '../../../scripts/context/sizeLayerContext.js';
import {productFamilyContext, ProductFamilyState} from '../../../scripts/context/productFamilyContext.js';
import {buildPdsUrl} from '../../../scripts/service/build-pds-url.js';
import {ActionItem, ActionMenu} from '../action-menu/action-menu.js';
import {pushToSnackbar} from '../../../scripts/service/snackbar-service.js';
import {WishlistAppConfig, wishlistAppConfigContext} from '../../../scripts/context/appConfigContext.js';
import {productsContext} from '../../../scripts/context/wishlistContext.js';
import {ProductIdentifier} from '../../../scripts/service/product-identifier.js';
import {addToListLayerContext, AddToListLayerState} from '../../../scripts/context/addToListLayerContext.js';
import {ProductsState} from '../../../scripts/context/wishlist-state.js';
import {productMenuContext} from '../../../scripts/context/menuContext.js';
import {ProductMenuState} from '../../../scripts/context/menu-state.js';
import baseLayerStyles from '../base-layer/base-layer.scss';
import {ADD_TO_WISHLIST_EVENT} from '../../../scripts/service/config.js';
import trashIcon from '@breuni-ui/bdk/dist/design-tokens/images/icons/o-ic-24_delete.svg';
import {hideIconFromAssistiveTechnology} from '../icon/icon-accessibility.js';

@customElement('ents-product-action-menu')
export class ProductActionMenu extends ActionMenu {
  protected testId: string = 'product-action-menu-layer';

  @consume({context: wishlistAppConfigContext})
  private wishlistAppConfig!: WishlistAppConfig;

  @consume({context: productMenuContext, subscribe: true})
  @property({attribute: false})
  private productMenuState!: ProductMenuState;

  @consume({context: productsContext, subscribe: true})
  @property({attribute: false})
  private productsState!: ProductsState;

  @consume({context: sizeLayerContext, subscribe: true})
  @property({attribute: false})
  private sizeLayerState!: SizeLayerState;

  @consume({context: productFamilyContext, subscribe: true})
  @property({attribute: false})
  private productFamilyState!: ProductFamilyState;

  @consume({context: addToListLayerContext, subscribe: true})
  @property({attribute: false})
  private addToListLayerState!: AddToListLayerState;

  @state()
  private shownImage: HTMLImageElement | null = null;

  static get styles(): CSSResult[] {
    return [css`${unsafeCSS(styles)}`, css`${unsafeCSS(typographyStyles)}`, css`${unsafeCSS(baseLayerStyles)}`];
  }

  private renderColor() {
    return this.productMenuState.context.product?.colorName ? html`
      <div class="ents-action-menu__product__color ents-copy-extra-small"
           data-test-ents-action-menu-color>
        ${this.messages['ents_wishlist_product_color']} ${this.productMenuState.context.product?.colorName}
      </div>
    ` : nothing;
  }

  private resetProductBanner() {
    // workaround to hide the product banner, otherwise the banner could be visible for wrong products
    // (edge case, only if user quickly switches between action menus and internet is slow)
    // not a 100% fix, because with very slow internet the problem can still happen
    // CAMP is discussing a better alternative than sending '-1' as syan
    document.dispatchEvent(new CustomEvent('bewe:ents:variantenwechsel', {detail: {syan: -1}})); // shared event used by other teams (e.g. BRTN, CAMP, KAUF)
  }

  protected updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);

    const previousProductMenuState: ProductMenuState | undefined = _changedProperties.get('productMenuState');
    if (previousProductMenuState != null
      && !previousProductMenuState.context.actionMenuOpen && this.productMenuState.context.actionMenuOpen
      && this.productMenuState.context.product) {
      this.fetchAndSaveProductFamilyInfo();
      this.changeGWPBanner();
      this.trackOpenEvent();
      this.showLayer = true;
      this.shownImage = this.shadowRoot
        ?.querySelector('.ents-action-menu__product__image ents-wishlist-image')
        ?.shadowRoot
        ?.querySelector('picture img') as HTMLImageElement;
    }
  }

  protected updateStateOnClose() {
    if (this.productMenuState.context.actionMenuOpen) {
      this.productMenuState.closeActionMenu();
      this.resetProductBanner();
    }
  }

  private fetchAndSaveProductFamilyInfo() {
    let fetchUrl = '';
    let displayType: 'productline' | 'capsule' | 'mixandmatch' | undefined = undefined;
    const articleIdWithProductFamilySet = this.identifyArticleIdWithProductFamily();
    if (this.isProductLineEnabled() && articleIdWithProductFamilySet !== undefined) {
      fetchUrl = `${this.wishlistAppConfig.productLineEndpoint}${articleIdWithProductFamilySet}/`;
      displayType = 'productline';
    } else if (this.isCapsuleEnabled() && articleIdWithProductFamilySet !== undefined) {
      fetchUrl = `${this.wishlistAppConfig.capsuleEndpoint}${articleIdWithProductFamilySet}/`;
      displayType = 'capsule';
    } else if (this.isMixAndMatchEnabled() && articleIdWithProductFamilySet !== undefined) {
      fetchUrl = `${this.wishlistAppConfig.mixAndMatchEndpoint}${articleIdWithProductFamilySet}/`;
      displayType = 'mixandmatch';
    } else {
      return;
    }

    fetchUrl += '?request-from=wishlist';
    fetch(fetchUrl)
      .then(response => (response.status === 200 ? response.json() : undefined))
      .then((json) => {
        this.productFamilyState.update({
          sliderData: json,
          displayType: displayType,
          beauty: this.productMenuState.context.product?.isBeauty,
        });
      });
  }

  private identifyArticleIdWithProductFamily() {
    if (this.productMenuState.context.product?.articleId) {
      return this.productMenuState.context.product.articleId;
    }
    // in case no size is selected yet, find first mm/line article
    return this.productMenuState.context.product?.articles.find(art => (art.mixAndMatchMessage !== undefined && art.mixAndMatchMessage !== '')
      || art.productLine || art.capsule)?.id;
  }

  protected renderInformation() {
    return html`
      <div class="ents-action-menu__product">
        <div class="ents-action-menu__product__image">
          <ents-wishlist-image
            image-url="${this.productMenuState.context.imageUrl}"
            sizes="(min-width: 1429px) calc((500px - var(--bdk-space-26) - var(--bdk-space-5)) * 0.4),
                    (min-width: 1160px) calc((35vw - var(--bdk-space-26) - var(--bdk-space-5)) * 0.4),
                    (min-width: 960px) calc((35vw - var(--bdk-space-18) - var(--bdk-space-5)) * 0.4),
                    calc((100vw - var(--bdk-space-9) - var(--bdk-space-5)) * 0.4)"
            alt-text="${this.productMenuState.context.product?.brandName} ${this.productMenuState.context.product?.name}"
            ?is-beauty="${this.productMenuState.context.product?.isBeauty === true}"
            brand-code="${this.productMenuState.context.product?.brandCode}"
          ></ents-wishlist-image>
        </div>
        <div class="ents-action-menu__product--information">
          <div class="ents-action-menu__product__brand ents-copy-small-bold ents-s:copy-bold"
               data-test-ents-action-menu-brand>
            ${this.productMenuState.context.product?.brandName}
          </div>
          <div class="ents-action-menu__product__name ents-copy-small"
               data-test-ents-action-menu-name>
            ${this.productMenuState.context.product?.name}
          </div>
          <div class="ents-action-menu__product__name ents-copy-small"
               data-test-ents-action-menu-subname>
            ${this.productMenuState.context.product?.subName}
          </div>
          ${this.renderColor()}
        </div>
      </div>`;
  }

  protected getApplicableActionItems() {
    return [
      ...(this.wishlistAppConfig.productTileRedesign ? [] : this.getSelectSizeItem()),
      ...this.getWvbItem(),
      ...this.getProductFamilyItem(),
      ...this.getVspItem(),
      ...this.getHvaItem(),
      ...this.getAddToListItem(),
      ...this.getShareItem(),
      ...this.getRemoveItem(),
    ];
  }

  private getSelectSizeItem(): ActionItem[] {
    if (this.productMenuState.context.product?.hasSizeOrQuantitySelection
      && this.productMenuState.context.product?.articles.length > 0
      && this.productMenuState.context.product?.isNotOneSize) {
      return [{
        label: this.messages['ents_wishlist_actionmenu_selectSize']!,
        arrow: true,
        trackingKey: 'size_selection',
        callback: () => {
          this.sizeLayerState.update({
            product: this.productMenuState.context.product,
            trackingContext: 'wishlist_action_menu',
            triggerA2C: false,
            open: true,
            selectedArticle: this.productMenuState.context.selectedArticle,
            backButtonCallback: this.open.bind(this),
          });
        },
      }];
    }
    return [];
  }

  private getWvbItem(): ActionItem[] {
    const isOutOfStock = this.productMenuState.context.selectedArticle !== null
      ? this.productMenuState.context.selectedArticle.isOutOfStock
      : this.productMenuState.context.product?.isOutOfStock;
    const isUnavailable = !this.productMenuState.context.product?.price;
    if (isOutOfStock && !isUnavailable) {
      return [{
        label: this.messages['ents_wishlist_actionmenu_wvb']!,
        arrow: true,
        trackingKey: 'wvb',
        callback: () => {
          if (this.productMenuState.context.selectedArticle !== null) {
            const payload = {
              vertriebsinfoId: this.productMenuState.context.selectedArticle.salesInformationId,
              source: 'wishlist_action_menu',
            };
            document.dispatchEvent(new CustomEvent('pruefen:best:wvb-overlay:open', {detail: payload}));
            document.dispatchEvent(new CustomEvent<ProductIdentifierContainer>('ents:cta-event-emitted', {
              detail: {
                identifier: ProductIdentifier.createByProduct(this.productMenuState.context.product).id,
              },
            }));
            this.setAttribute('data-test-ents-notify-me-payload-emitted', JSON.stringify(payload));
          } else {
            this.sizeLayerState.update({
              product: this.productMenuState.context.product,
              trackingContext: 'wishlist_action_menu',
              triggerA2C: false,
              open: true,
              selectedArticle: this.productMenuState.context.selectedArticle,
              backButtonCallback: this.open.bind(this),
            });
          }
        },
      }];
    }
    return [];
  }

  private isMixAndMatchEnabled(): boolean {
    const mixAndMatchMessage = this.productMenuState.context.product?.mixAndMatchMessage;
    return mixAndMatchMessage !== undefined && mixAndMatchMessage !== '';
  }

  private isProductLineEnabled(): boolean {
    const productLineExists = this.productMenuState.context.product?.productLine;
    return productLineExists !== undefined && productLineExists;
  }

  private isCapsuleEnabled(): boolean {
    const capsuleExists = this.productMenuState.context.product?.capsule;
    return capsuleExists !== undefined && capsuleExists;
  }

  private getProductFamilyItem(): ActionItem[] {
    if (this.isMixAndMatchEnabled()) {
      return [{
        label: this.messages['ents_wishlist_actionmenu_mixandmatch']!,
        arrow: true,
        trackingKey: 'mix&match',
        callback: () => {
          this.productFamilyState.update({
            open: true,
            backButtonCallback: this.open.bind(this)
          });
        },
      }];
    }
    if (this.isCapsuleEnabled()) {
      return [{
        label: this.messages['ents_wishlist_actionmenu_capsule']!,
        arrow: true,
        trackingKey: 'capsule',
        callback: () => {
          this.productFamilyState.update({
            open: true,
            backButtonCallback: this.open.bind(this)
          });
        },
      }];
    }
    if (this.isProductLineEnabled()) {
      return [{
        label: this.messages['ents_wishlist_actionmenu_productline']!,
        arrow: true,
        trackingKey: 'productline',
        callback: () => {
          this.productFamilyState.update({
            open: true,
            backButtonCallback: this.open.bind(this)
          });
        },
      }];
    }
    return [];
  }

  private getHvaItem(): ActionItem[] {
    const product = this.productMenuState.context.product;
    const articles = product?.articles || [];
    const currentArticle = this.productMenuState.context.selectedArticle ?? articles[0];
    const enabled = articles.length > 0 && product?.isHvaButtonEnabled
      && product?.hasStockInAnyStore && !currentArticle.isMarketplace;
    if (enabled) {
      return [{
        label: this.messages['ents_wishlist_actionmenu_or']!,
        arrow: true,
        trackingKey: 'hva',
        callback: () => {
          const articleId = currentArticle.id;
          const payload = {detail: {articleId}};
          document.dispatchEvent(new CustomEvent('ents:hva-modal:open', payload));
          this.setAttribute('data-test-ents-hva-payload-emitted', JSON.stringify(payload));
          trackClickAndReserveEvent();
        },
      }];
    }
    return [];
  }

  private getVspItem(): ActionItem[] {
    if (this.productMenuState.context.product?.isVspButtonEnabled) {
      return [{
        label: this.messages['ents_wishlist_actionmenu_vsp']!,
        arrow: true,
        trackingKey: 'vsp',
        callback: () => {
          const payload = {
            syan: this.productMenuState.context.product?.syan,
            ...(this.productMenuState.context.product?.colorId ? {farbId: this.productMenuState.context.product?.colorId} : {}),
            listName: 'wishlist_actionmenu',
          };
          document.dispatchEvent(new CustomEvent('suchen:vsp:alternativeprodukte', {detail: payload}));
          this.setAttribute('data-test-ents-vsp-payload-emitted', JSON.stringify(payload));
        },
      }];
    }
    return [];
  }

  private getShareItem(): ActionItem[] {
    const product = this.productMenuState.context.product;
    const itemInfoToShare = {
      url: buildPdsUrl(
        product?.pdsUrlBasePath,
        product?.articleId,
        product?.colorId,
      ),
      text: this.messages['ents_wishlist_sharing_article_body']
    };
    if (product?.price !== undefined && navigator.share !== undefined && navigator.canShare(itemInfoToShare)) {
      return [{
        label: this.messages['ents_wishlist_actionmenu_shareItem']!,
        arrow: false,
        trackingKey: 'share',
        callback: () => {
          navigator.share(itemInfoToShare);
        },
      }];
    }
    return [];
  }

  private getRemoveItem(): ActionItem[] {
    if (this.productsState.isShared()) {
      return [];
    }
    const product = this.productMenuState.context.product;
    const collection = this.productMenuState.context.collection;
    const labelText = collection
      ? this.messages['ents_wishlist_actionmenu_removeFromCollection']!
      : this.messages['ents_wishlist_actionmenu_removeItem']!;
    const callbackRemoveAction = collection ?
      () => this.productMenuState.openRemoveArticleMenu(product!, collection, this.open.bind(this)) :
      () => {
        const selectedArticle = this.productMenuState.context.selectedArticle;
        this.removeFromWishlist(product!, selectedArticle);
        this.pushActionToSnackbar(() => {
          this.undoRemove(product!, selectedArticle);
        });
      };

    return [{
      label: this.wishlistAppConfig.productTileRedesign ? html`${hideIconFromAssistiveTechnology(trashIcon())}<span>${labelText}</span>` : labelText,
      arrow: false,
      trackingKey: 'remove',
      callback: callbackRemoveAction
    }];
  }

  private pushActionToSnackbar(undoAction?: () => void) {
    let articleDeletedText = this.messages['ents_wishlist_delete_title'];
    if (this.isOnAnyCollection()) {
      articleDeletedText = this.messages['ents_wishlist_delete_all_title'];
    }
    pushToSnackbar(
      articleDeletedText!,
      this.shownImage!.currentSrc,
      undoAction !== undefined ? this.messages['ents_wishlist_delete_buttontext'] : undefined,
      undoAction,
    );
  }

  private isOnAnyCollection(): boolean {
    return this.productsState.isProductInAnyCollection(
      ProductIdentifier.createByProduct(this.productMenuState.context.product)
    );
  }

  private removeFromWishlist(product: Product, selectedArticle: Article | null) {
    const data = this.getWishlistProductData(product, selectedArticle);
    const trackingCallback = () => trackRemoveFromWishlist(
      data.syan,
      data.colorId,
      data.articleId,
      data.tracking,
      this.isOnAnyCollection());
    wishlistService.removeFromWishlist(data, trackingCallback);
  }

  private getWishlistProductData(product: Product, selectedArticle: Article | null) {
    return {
      identifier: ProductIdentifier.createByProduct(product).id,
      syan: product.syan,
      colorId: product.colorId,
      articleId: selectedArticle?.id,
      added: product.added,
      purchased: product.purchased,
      collectionReferences: product.collectionReferences,
      tracking: {
        placement: 'wishlist',
        context: 'wishlist_action_menu',
        added: product.added,
      },
    };
  }

  private undoRemove(product: Product, selectedArticle: Article | null) {
    trackSnackbarUndoWishlistItemDelete(product);
    document.dispatchEvent(
      new CustomEvent(ADD_TO_WISHLIST_EVENT, {detail: this.getWishlistProductData(product, selectedArticle)})
    );
  }

  private trackOpenEvent() {
    const items = this.getApplicableActionItems().map(item => item.trackingKey);
    trackProductActionMenuOpen(
      items,
      this.productMenuState.context.product!.syan,
      this.productMenuState.context.selectedArticle,
      this.productMenuState.context.product?.colorId
    );
  }

  private getAddToListItem(): ActionItem[] {
    return [{
      label: `${this.messages['ents_wishlist_addToList_add_to_list']}`,
      arrow: true,
      trackingKey: 'add_to_collection',
      callback: () => {
        this.addToListLayerState.update({
          open: true,
          backButtonCallback: this.open.bind(this)
        });
      },
    }];
  }

  protected open() {
    this.productMenuState.openActionMenu(
      this.productMenuState.context.product!,
      this.productMenuState.context.collection,
      this.productMenuState.context.imageUrl,
      this.productMenuState.context.selectedArticle
    );
  }

  private changeGWPBanner() {
    const gwpbanner = document.querySelector('camp-gwpbanner');
    if (gwpbanner) {
      const {syan, colorId, articleId} = this.productMenuState.context.product || {};

      if (syan !== undefined) {
        gwpbanner.setAttribute('syan', syan.toString());
      }

      if (colorId !== undefined) {
        gwpbanner.setAttribute('farb-id', colorId);
      } else {
        gwpbanner.removeAttribute('farb-id');
      }

      if (articleId !== undefined) {
        gwpbanner.setAttribute('artikel-id', articleId);
      } else {
        gwpbanner.removeAttribute('artikel-id');
      }
    }
  }
}
